import React from 'react';
import { Offer, ProductVariant } from '../../types';
import { MoneyFormat, OfferPrice, PriceBeforeDiscount } from '../elements';
import { getDiscountedPrice } from '../../dissolve/helpers';
import { useAppsInfo } from '../../contexts/AppsInfoContext';
import {
  getIntelligemsComparePriceByVariantId,
  getIntelligemsVariantPrice,
} from '../../shared/product-page-placement/intelligemsUtils';
import { logInfo } from '../../shared/utils/logging';

interface Props {
  offer: Partial<Offer>;
  variant: ProductVariant;
  quantity?: number;
}

const ProductOfferPricing = ({ offer, variant }: Props) => {
  const appsInfo = useAppsInfo();

  logInfo(
    `ProductOfferPricing render: variantId=${variant?.id}, variantTitle=${variant?.title}`,
  );

  const { apply_discount } = offer;
  const totalPrice = getDiscountedPrice(
    offer,
    variant,
    1,
    appsInfo.is_intelligems_enabled,
  );

  const intelligemsPriceForVariant = getIntelligemsVariantPrice(
    appsInfo.is_intelligems_enabled,
    variant?.id,
    variant?.title,
  );
  const intelligemsComparePriceForVariant =
    getIntelligemsComparePriceByVariantId(
      appsInfo.is_intelligems_enabled,
      variant?.id,
      variant?.title,
    );

  // Follow two distinct paths based on Intelligems availability
  let priceBeforeDiscount = null;

  if (apply_discount) {
    priceBeforeDiscount = (
      <MoneyFormat
        amount={Number(intelligemsPriceForVariant ?? variant.price) / 100}
      />
    );
  } else if (intelligemsPriceForVariant !== undefined) {
    // Path 1: Intelligems provides variant price - only use Intelligems compare price or nothing
    if (
      intelligemsComparePriceForVariant !== undefined &&
      intelligemsComparePriceForVariant !== null &&
      Number(intelligemsComparePriceForVariant) >
        Number(intelligemsPriceForVariant)
    ) {
      priceBeforeDiscount = (
        <MoneyFormat amount={Number(intelligemsComparePriceForVariant) / 100} />
      );
    }
  } else {
    // Path 2: Intelligems doesn't provide variant price - use Shopify's compare_at_price
    if (
      variant.compare_at_price &&
      Number(variant.compare_at_price) > Number(variant.price)
    ) {
      priceBeforeDiscount = (
        <MoneyFormat amount={Number(variant.compare_at_price) / 100} />
      );
    }
  }

  return (
    <div className="Offer-Prices flex">
      <OfferPrice>
        <MoneyFormat amount={totalPrice} />
      </OfferPrice>
      {priceBeforeDiscount && (
        <PriceBeforeDiscount>{priceBeforeDiscount}</PriceBeforeDiscount>
      )}
    </div>
  );
};

export default ProductOfferPricing;
